import { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';

import Payment, { SdkLoader } from '@solidgate/react-sdk';
SdkLoader.load("https://cdn.charge-auth.com/js/form.js");

import { useOnboarding } from '../../../context/OnboardingContext';
import { logPurchaseCompletedSuccess, paymentStore, solidgateApplePayContainerRef, solidgateGooglePayContainerRef } from './StripeHelper';
import { createSolidgateIntent } from '../../../services/api';
import { trackPurchaseStarted, trackInitiateCheckout } from '../../../services/analytics';
import AccordionPanel from './shared/AccordionPanel';
import { showPaymentErrorPopup, hidePaymentErrorPopup } from '../../../stores/paymentErrorPopupStore';

/** Max wait for Solidgate onMounted per wallet; then treat channel as resolved without button. */
const SOLIDGATE_WALLET_RESOLVE_MS = 1500;

function finishSolidgateWalletPhaseIfReady() {
    const s = paymentStore.getState();
    if (s.isSolidgateAppleWalletResolved && s.isSolidgateGoogleWalletResolved) {
        paymentStore.setState({ isSolidgateLoading: false });
    }
}

const SolidgateCard = ({ isExpanded, onSelect }) => {
    const {
        userID,
        selectedPlan,
        onboardingId,
        formData,
        globalAnswers,
        goToNextScreen,
        goToScreenWithName,
        urlParams,
    } = useOnboarding();

    const [merchantData, setMerchantData] = useState(null);
    const [formKey, setFormKey] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [retryNonce, setRetryNonce] = useState(0);
    const appleWalletTimeoutRef = useRef(null);
    const googleWalletTimeoutRef = useRef(null);

    const productPriceId = selectedPlan?.solidgate?.productPriceId;
    const couponId = selectedPlan?.solidgate?.couponId;
    const email = formData.get('email') ?? null;

    /**
     * Drop the current intent/form and request a fresh one with a new order_id.
     * Wired into the "Try again" button of the payment-error popup.
     */
    const recreateIntent = useCallback(() => {
        console.log('[SolidgateCard] recreateIntent: triggering retry');
        setIsProcessing(false);
        paymentStore.setState({ isSolidgateWalletProcessing: false });
        setRetryNonce((n) => n + 1);
    }, []);

    useLayoutEffect(() => {
        if (!productPriceId || !email) return;

        setIsLoading(true);
        setMerchantData(null);
        paymentStore.setState({
            isSolidgateLoading: true,
            isSolidgateApplePayMounted: false,
            isSolidgateGooglePayMounted: false,
            isSolidgateAppleWalletResolved: false,
            isSolidgateGoogleWalletResolved: false,
            isSolidgateWalletProcessing: false,
        });

        return () => {
            if (appleWalletTimeoutRef.current) {
                clearTimeout(appleWalletTimeoutRef.current);
                appleWalletTimeoutRef.current = null;
            }
            if (googleWalletTimeoutRef.current) {
                clearTimeout(googleWalletTimeoutRef.current);
                googleWalletTimeoutRef.current = null;
            }
        };
    }, [productPriceId, couponId, email, userID, onboardingId, retryNonce]);

    useEffect(() => {
        if (!productPriceId || !email) return;

        let cancelled = false;

        const body = {
            userID,
            email,
            onboardingID: onboardingId,
            productPriceId,
            goal: globalAnswers.get('goal')?.value || 'unknown',
            voice: globalAnswers.get('voice')?.value || 'unknown',
            queryParams: urlParams || {},
        };
        if (couponId) body.couponId = couponId;

        console.log('[SolidgateCard] createIntent for priceId=%s, retryNonce=%d', productPriceId, retryNonce);

        createSolidgateIntent(body)
            .then((data) => {
                if (cancelled) return;
                console.log('[SolidgateCard] createIntent response:', JSON.stringify(data));
                if (data?.success) {
                    setMerchantData({
                        merchant: data.merchant,
                        signature: data.signature,
                        paymentIntent: data.paymentIntent,
                    });
                    setFormKey((k) => k + 1);
                } else {
                    console.error('[SolidgateCard] createIntent error:', JSON.stringify(data));
                    setIsLoading(false);
                    paymentStore.setState({
                        isSolidgateLoading: false,
                        isSolidgateAppleWalletResolved: true,
                        isSolidgateGoogleWalletResolved: true,
                        isSolidgateApplePayMounted: false,
                        isSolidgateGooglePayMounted: false,
                    });
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    console.error('[SolidgateCard] createIntent failed:', err);
                    setIsLoading(false);
                    paymentStore.setState({
                        isSolidgateLoading: false,
                        isSolidgateAppleWalletResolved: true,
                        isSolidgateGoogleWalletResolved: true,
                        isSolidgateApplePayMounted: false,
                        isSolidgateGooglePayMounted: false,
                    });
                }
            });



        return () => {
            cancelled = true;
            if (appleWalletTimeoutRef.current) {
                clearTimeout(appleWalletTimeoutRef.current);
                appleWalletTimeoutRef.current = null;
            }
            if (googleWalletTimeoutRef.current) {
                clearTimeout(googleWalletTimeoutRef.current);
                googleWalletTimeoutRef.current = null;
            }
        };
    }, [productPriceId, couponId, email, userID, onboardingId, retryNonce]);

    const clearAppleWalletTimeout = useCallback(() => {
        if (appleWalletTimeoutRef.current) {
            clearTimeout(appleWalletTimeoutRef.current);
            appleWalletTimeoutRef.current = null;
        }
    }, []);

    const clearGoogleWalletTimeout = useCallback(() => {
        if (googleWalletTimeoutRef.current) {
            clearTimeout(googleWalletTimeoutRef.current);
            googleWalletTimeoutRef.current = null;
        }
    }, []);

    const scheduleWalletResolveTimeouts = useCallback(() => {
        clearAppleWalletTimeout();
        clearGoogleWalletTimeout();

        appleWalletTimeoutRef.current = setTimeout(() => {
            appleWalletTimeoutRef.current = null;
            const st = paymentStore.getState();
            if (!st.isSolidgateAppleWalletResolved) {
                paymentStore.setState({ isSolidgateAppleWalletResolved: true });
            }
            finishSolidgateWalletPhaseIfReady();
        }, SOLIDGATE_WALLET_RESOLVE_MS);

        googleWalletTimeoutRef.current = setTimeout(() => {
            googleWalletTimeoutRef.current = null;
            const st = paymentStore.getState();
            if (!st.isSolidgateGoogleWalletResolved) {
                paymentStore.setState({ isSolidgateGoogleWalletResolved: true });
            }
            finishSolidgateWalletPhaseIfReady();
        }, SOLIDGATE_WALLET_RESOLVE_MS);
    }, [clearAppleWalletTimeout, clearGoogleWalletTimeout]);

    const handleMounted = useCallback((event) => {
        console.log('[SolidgateCard] onMounted event:', JSON.stringify(event));
        const entity = event?.entity;
        if (entity === 'applebtn') {
            clearAppleWalletTimeout();
            paymentStore.setState({
                isSolidgateApplePayMounted: true,
                isSolidgateAppleWalletResolved: true,
            });
            finishSolidgateWalletPhaseIfReady();
        } else if (entity === 'googlebtn') {
            clearGoogleWalletTimeout();
            paymentStore.setState({
                isSolidgateGooglePayMounted: true,
                isSolidgateGoogleWalletResolved: true,
            });
            finishSolidgateWalletPhaseIfReady();
        }
        if (entity === 'form') {
            setIsLoading(false);
            scheduleWalletResolveTimeouts();
        }
    }, [clearAppleWalletTimeout, clearGoogleWalletTimeout, scheduleWalletResolveTimeouts]);

    const handleInteraction = useCallback((event) => {
        const name = event?.target?.name;
        const interaction = event?.target?.interaction;
        if (name !== 'applePay' && name !== 'googlePay') return;
        if (interaction === 'click') {
            paymentStore.setState({ isSolidgateWalletProcessing: true });
        } else if (interaction === 'pageClose') {
            paymentStore.setState({ isSolidgateWalletProcessing: false });
        }
    }, []);

    const handleSubmit = useCallback((event) => {
        setIsProcessing(true);

        const entity = event?.entity;
        if (entity === 'applebtn' || entity === 'googlebtn') {
            paymentStore.setState({ isSolidgateWalletProcessing: true });
        }

        try {
            trackInitiateCheckout(formData.get('email') ?? 'unknown');
        } catch (error) {
            console.error('Error logging initiate checkout:', error);
        }

        try {
            trackPurchaseStarted({
                type: 'solidgateCard',
                onboardingId,
                priceID: selectedPlan?.solidgate?.productPriceId,
            });
        } catch (error) {
            console.error('Error tracking purchase started:', error);
        }
    }, [onboardingId, selectedPlan, formData]);

    const handleSuccess = useCallback((event) => {
        const order = event?.order || {};
        const amount = order.amount || 0;
        const email = formData.get('email') ?? 'unknown';

        localStorage.setItem('paymentProvider', 'solidgate');

        logPurchaseCompletedSuccess(
            onboardingId,
            selectedPlan?.solidgate?.productPriceId,
            selectedPlan?.solidgate?.couponId || null,
            amount,
            urlParams,
            email
        );

        setIsProcessing(false);
        paymentStore.setState({ isSolidgateWalletProcessing: false });
        goToNextScreen();
    }, [onboardingId, selectedPlan, urlParams, formData, goToNextScreen, goToScreenWithName]);

    const handleFail = useCallback((event) => {
        console.log('[SolidgateCard] onFail event:', JSON.stringify(event));
        setIsProcessing(false);
        paymentStore.setState({ isSolidgateWalletProcessing: false });

        showPaymentErrorPopup({
            message: event.message ?? 'Unknown error',
            buttons: [
                { text: 'Try again', onClick: () => recreateIntent() },
                { text: 'Cancel', onClick: () => recreateIntent() },
            ],
        });

    }, []);

    const handleError = useCallback((event) => {
        console.error('[SolidgateCard] onError event:', JSON.stringify(event));
        setIsProcessing(false);
        paymentStore.setState({ isSolidgateWalletProcessing: false });
    }, []);

    if (!selectedPlan?.solidgate?.productPriceId) {
        console.log('[SolidgateCard] Render: returning null (no productPriceId)');
        return null;
    }

    console.log('[SolidgateCard] Render: merchantData=%s, isLoading=%s, formKey=%d',
        merchantData ? 'SET' : 'NULL', isLoading, formKey);

    console.log('solidgateApplePayContainerRef', solidgateApplePayContainerRef);
    console.log('solidgateGooglePayContainerRef', solidgateGooglePayContainerRef);

    
    return (
        <section
            className="item"
            data-state={isExpanded ? 'expanded' : 'collapsed'}
            onClick={onSelect}
        >
            <button type="button" className="trigger" aria-expanded={isExpanded}>
                <div className="radio-circle"></div>
                <div className="payment-icons">
                    <div className="image-wrap svelte-grrljh">
                        <img
                            className="payment-method-icon svelte-171041c"
                            src="/assets/images/cards.webp"
                            alt="visa, mastercard"
                            style={{ maxHeight: 'calc(30 * var(--px))' }}
                        />
                    </div>
                </div>
            </button>

            <AccordionPanel expanded={isExpanded}>
                <div className="panel" role="region">
                    <div style={{ position: 'relative', minHeight: '200px' }}>
                        {isLoading && (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    minHeight: '200px',
                                }}
                            >
                                <div className="spinner"></div>
                            </div>
                        )}

                        {merchantData && (
                            <Payment
                                key={formKey}
                                merchantData={merchantData}
                                formParams={{
                                    allowSubmit: true,
                                    formTypeClass: 'default',
                                    isCardHolderVisible: true,
                                    autoFocus: false,
                                }}
                                googlePayButtonParams={{ enabled: true }}
                                applePayButtonParams={{
                                    enabled: true,
                                    type: 'continue',
                                    color: 'white',
                                    integrationType: 'js'                                    
                                }}
                                applePayContainerRef={solidgateApplePayContainerRef}
                                googlePayContainerRef={solidgateGooglePayContainerRef}
                                onMounted={handleMounted}
                                onSuccess={handleSuccess}
                                onFail={handleFail}
                                onSubmit={handleSubmit}
                                onInteraction={handleInteraction}
                                onError={handleError}
                            />
                        )}
                    </div>
                    <div className="footer svelte-p3ln32">
                        <span className="svelte-kfp6vj">
                            Your payment information is secure with SSL/TLS encryption
                        </span>
                    </div>
                </div>
            </AccordionPanel>
        </section>
    );
};

export default SolidgateCard;
